import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ban,
  CalendarDays,
  ChevronRight,
  Clock,
  Flag,
  MapPin,
  Star,
  Trophy,
} from "lucide-react";
import { apiFetch } from "@/lib/api";
import {
  formatBRL,
  formatDateRangeUTC,
  formatDeadline,
  timeRemaining,
} from "@/lib/format";
import {
  collectMyPickSnapshots,
  formatPosition,
  formatRoundStatus,
  formatScore,
  liveTop3Ids,
  myPicksOutsideTop,
  type LiveEntry,
} from "@/lib/liveLeaderboard";
import { selectFeatured } from "@/lib/tournaments";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { ScreenSpinner } from "@/components/ui/Spinner";
import type { FinanceSettings, Tournament } from "@/types";

// Quantas posições do leaderboard oficial aparecem na Home.
const TOP_POSITIONS = 10;

type MyBet = {
  id: string;
  tournamentId: string;
  playerIds: string[];
  playerNames: string[];
  amount: number;
  createdAt: string;
};

// Status pill do hero card — réplica do app original: fundo sólido accent
// com pontinho + "FECHA EM {countdown}" enquanto aberto; destructive quando
// encerrado/terminado; âmbar-marrom quando o admin desativou as apostas.
function StatusPill({ tournament }: { tournament: Tournament }) {
  const base =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-[5px] text-[10px] font-bold tracking-wider";
  if (tournament.status === "settled" || tournament.finishedAt) {
    return (
      <span className={`${base} bg-destructive text-destructive-foreground`}>
        <Flag size={11} /> EVENTO TERMINADO
      </span>
    );
  }
  if (!tournament.betsEnabled) {
    // Cores hardcoded no original (app/(tabs)/index.tsx): #B45309/#FEF3C7.
    return (
      <span className={`${base} bg-[#B45309] text-[#FEF3C7]`}>
        <Ban size={11} /> APOSTAS DESATIVADAS
      </span>
    );
  }
  const remaining = timeRemaining(tournament.deadline);
  const isOpen = remaining !== "Apostas encerradas";
  return (
    <span
      className={`${base} ${
        isOpen
          ? "bg-accent text-accent-foreground"
          : "bg-destructive text-destructive-foreground"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isOpen ? "bg-accent-foreground" : "bg-destructive-foreground"
        }`}
      />
      {isOpen ? `FECHA EM ${remaining}` : "APOSTAS ENCERRADAS"}
    </span>
  );
}

// Célula de status da rodada: bandeira + buraco atual, "F" ou tee time.
function RoundStatusCell({ entry }: { entry: LiveEntry }) {
  const status = formatRoundStatus(entry);
  if (!status) return <span className="w-10 shrink-0" />;
  return (
    <span className="flex w-10 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
      {status.flag && <Flag size={11} />}
      {status.text}
    </span>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [settings, setSettings] = useState<FinanceSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [myBets, setMyBets] = useState<MyBet[] | null>(null);
  const [leaderboard, setLeaderboard] = useState<LiveEntry[]>([]);
  const [leaderboardFetched, setLeaderboardFetched] = useState(false);

  useEffect(() => {
    Promise.all([
      apiFetch<Tournament[]>("/api/tournaments", { auth: false }),
      apiFetch<FinanceSettings>("/api/settings", { auth: false }),
    ])
      .then(([t, s]) => {
        setTournaments(t);
        setSettings(s);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = useMemo(() => selectFeatured(tournaments), [tournaments]);

  // Palpites do usuário (todos) — usados para o card "Meus palpites" do
  // torneio em destaque e para o contador "x de N palpites utilizados".
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    apiFetch<{ bets: MyBet[] }>("/api/me/bets")
      .then((res) => {
        if (!cancelled) setMyBets(res.bets);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Placar ao vivo do torneio em destaque, com poll de 60s (igual ao app
  // original) para posição/score/buraco acompanharem o feed do servidor.
  useEffect(() => {
    if (!featured?.id) return;
    let cancelled = false;
    const load = () => {
      apiFetch<LiveEntry[]>(
        `/api/tournaments/${encodeURIComponent(featured.id)}/leaderboard`,
        { auth: false },
      )
        .then((rows) => {
          if (!cancelled) {
            setLeaderboard(rows);
            setLeaderboardFetched(true);
          }
        })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [featured?.id]);

  // Só para decidir se o link "Próximos Torneios" aparece — a lista em si
  // mora na página /proximos-torneios.
  const upcoming = useMemo(
    () =>
      tournaments.filter(
        (t) => t.status === "upcoming" && t.id !== featured?.id,
      ),
    [tournaments, featured],
  );

  const featuredBets = useMemo(
    () => (myBets ?? []).filter((b) => b.tournamentId === featured?.id),
    [myBets, featured?.id],
  );
  const totalBetAmount = featuredBets.reduce((sum, b) => sum + b.amount, 0);

  // Posição/score/buraco só fazem sentido com torneio em andamento (ou
  // recém-encerrado com o feed ainda carregado).
  const showLive =
    featured?.status === "active" || featured?.status === "settled";
  const leaderboardLoaded = leaderboardFetched && leaderboard.length > 0;
  // Todos os jogadores ocupando as TOP_POSITIONS primeiras colocações — com
  // empates a lista pode ter mais linhas que posições.
  const topLeaderboard = leaderboard.filter(
    (e) => e.position !== null && e.position <= TOP_POSITIONS,
  );
  const pickSnapshots = useMemo(
    () => collectMyPickSnapshots(featuredBets),
    [featuredBets],
  );
  const picksBelowTop = useMemo(
    () => myPicksOutsideTop(pickSnapshots, leaderboard, topLeaderboard),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pickSnapshots, leaderboard],
  );
  // Destaque âmbar: 3 primeiros do leaderboard (IDs do provedor, sem
  // depender de resolução de nome nem do check position <= 3).
  const top3 = useMemo(() => liveTop3Ids(leaderboard), [leaderboard]);
  const maxBets = settings?.maxBetsPerTournament ?? 0;
  const myBetCount = myBets === null ? null : featuredBets.length;

  if (loading) return <ScreenSpinner />;

  return (
    <div className="flex flex-col gap-5 px-5 pt-6">
      <header className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-medium tracking-[2px] text-tint">
            BOLÃO PGA
          </p>
          <h1 className="text-xl font-bold">
            Olá, {user?.nickname ?? user?.name?.split(" ")[0]}
          </h1>
        </div>
        {/* Créditos = apostável; Disponível = dinheiro (sacável). */}
        <div className="flex gap-2">
          <div className="rounded-(--radius-field) bg-surface-alt px-3 py-2 text-right">
            <p className="text-[10px] tracking-wide text-muted-foreground">
              CRÉDITOS
            </p>
            <p className="text-sm font-bold" data-testid="home-credits">
              {formatBRL(user?.creditBalance ?? 0)}
            </p>
          </div>
          <div className="rounded-(--radius-field) bg-surface-alt px-3 py-2 text-right">
            <p className="text-[10px] tracking-wide text-muted-foreground">
              DISPONÍVEL
            </p>
            <p className="text-sm font-bold" data-testid="home-available">
              {formatBRL(user?.availableBalance ?? 0)}
            </p>
          </div>
        </div>
      </header>

      {featured ? (
        <Card className="bg-hero p-5">
          <div className="mb-3 flex items-center justify-between">
            <StatusPill tournament={featured} />
            {featured.jackpotIn > 0 && (
              <Chip tone="accent">
                <Trophy size={12} /> JACKPOT {formatBRL(featured.jackpotIn)}
              </Chip>
            )}
          </div>

          <h2 className="text-lg font-bold">{featured.name}</h2>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin size={14} /> {featured.course} · {featured.location}
          </p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-(--radius-field) bg-surface-alt p-3">
              <p className="flex items-center gap-1 text-[10px] tracking-wide text-muted-foreground">
                <CalendarDays size={12} /> DATAS
              </p>
              <p className="mt-1 text-sm font-semibold">
                {formatDateRangeUTC(featured.startDate, featured.endDate)}
              </p>
            </div>
            <div className="rounded-(--radius-field) bg-surface-alt p-3">
              <p className="flex items-center gap-1 text-[10px] tracking-wide text-muted-foreground">
                <Clock size={12} /> PALPITES ATÉ
              </p>
              <p className="mt-1 text-sm font-semibold">
                {formatDeadline(featured.deadline)}
              </p>
            </div>
          </div>

          {/* Só a premiação (pote mínimo), centralizada e maior (12px → 16px). */}
          {settings && (
            <p className="mt-4 text-center text-base text-muted-foreground">
              Premiação:{" "}
              <b className="text-foreground">
                {formatBRL(featured.minPot ?? settings.minPotDefault)}
              </b>
            </p>
          )}

          <Button
            title="Fazer palpite"
            disabled={
              !featured.betsEnabled ||
              featured.status === "settled" ||
              new Date(featured.deadline).getTime() < Date.now() ||
              (maxBets > 0 && (myBetCount ?? 0) >= maxBets)
            }
            onClick={() => navigate(`/palpite/${featured.id}`)}
            className="mt-4 w-full"
          />
          {maxBets > 0 && myBetCount !== null && (
            <p
              className="mt-2 text-center text-xs font-medium text-muted-foreground"
              data-testid="bet-counter"
            >
              {myBetCount} de {maxBets} palpites utilizados
            </p>
          )}
        </Card>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nenhum torneio disponível.
        </p>
      )}

      {/* Mesmo toggle do app original (heroExpandToggle): fora do hero card,
          levando à página /proximos-torneios. */}
      {upcoming.length > 0 && (
        <Link
          to="/proximos-torneios"
          className="-mt-2 flex items-center justify-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          Próximos Torneios <ChevronRight size={16} />
        </Link>
      )}

      {/* Meus palpites do torneio em destaque: posição ao vivo (T#) na
          primeira coluna, nome (âmbar quando no Top 3 ao vivo) e, à
          direita, buraco atual da rodada e score (mesma ordem do
          Placar ao vivo). */}
      {featured && featuredBets.length > 0 && (
        <section data-testid="home-my-bets">
          <SectionHeader
            title={featuredBets.length === 1 ? "Meu palpite" : "Meus palpites"}
            caption={
              featuredBets.length === 1
                ? `Total apostado: ${formatBRL(totalBetAmount)}`
                : `${featuredBets.length} palpites · Total apostado: ${formatBRL(totalBetAmount)}`
            }
          />
          <div className="flex flex-col gap-2.5">
            {featuredBets.map((bet, idx) => (
              <Card key={bet.id} className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold">
                    {featuredBets.length === 1 ? "Palpite" : `Palpite ${idx + 1}`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatBRL(bet.amount)}
                  </p>
                </div>
                <div className="mt-3 flex flex-col gap-1.5">
                  {bet.playerIds.map((pid, i) => {
                    const entry = leaderboard.find((e) => e.playerId === pid);
                    // O feed do Data Golf omite quem perdeu o corte: com o
                    // leaderboard carregado, pick ausente (ou posição null)
                    // é CUT/WD/DQ.
                    const isCut =
                      showLive &&
                      leaderboardLoaded &&
                      (!entry || entry.position === null);
                    const nameClass = top3.has(pid)
                      ? "text-accent"
                      : isCut
                        ? "text-muted-foreground"
                        : "";
                    return (
                      <div key={pid} className="flex items-center gap-2">
                        {showLive && leaderboardLoaded && (
                          <span
                            className={`w-8 shrink-0 text-xs font-semibold ${
                              isCut
                                ? "text-destructive"
                                : "text-muted-foreground"
                            }`}
                          >
                            {isCut
                              ? "CUT"
                              : entry && entry.position !== null
                                ? formatPosition(entry.position)
                                : "—"}
                          </span>
                        )}
                        <p
                          className={`min-w-0 flex-1 truncate text-sm font-semibold ${nameClass}`}
                        >
                          {bet.playerNames[i]?.trim() || pid}
                        </p>
                        {showLive && entry && entry.position !== null && (
                          <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                            <RoundStatusCell entry={entry} />
                            {formatScore(entry.totalScore)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Placar ao vivo — Top 10 do leaderboard oficial + picks do usuário
          fora do top (sem duplicar quem já aparece, só realce + badge ★). */}
      {featured && (
        <section data-testid="home-leaderboard">
          <SectionHeader
            title="Placar ao vivo"
            caption={`Top ${TOP_POSITIONS} do leaderboard oficial`}
            action={
              featured.currentRound != null &&
              Number.isInteger(featured.currentRound) &&
              featured.currentRound >= 1 &&
              featured.currentRound <= 4 ? (
                <Chip tone="accent">{`R${featured.currentRound}`}</Chip>
              ) : undefined
            }
          />
          {topLeaderboard.length === 0 ? (
            <Card className="flex items-center gap-2.5 px-4 py-5">
              <Clock size={16} className="shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {showLive && !leaderboardFetched
                  ? "Carregando placar..."
                  : "Aguardando início"}
              </p>
            </Card>
          ) : (
            <Card className="overflow-hidden p-0">
              {topLeaderboard.map((entry, idx) => {
                const isMyPick = pickSnapshots.has(entry.playerId);
                // Formato PGA: a posição aparece só na primeira linha do
                // grupo empatado; as demais ficam em branco.
                const positionLabel =
                  entry.position !== null &&
                  (idx === 0 ||
                    topLeaderboard[idx - 1]!.position !== entry.position)
                    ? formatPosition(entry.position)
                    : "";
                return (
                  <div
                    key={`${entry.playerId}-${entry.position}`}
                    className={`flex items-center gap-2 px-4 py-2.5 ${
                      idx > 0 ? "border-t border-border" : ""
                    }`}
                  >
                    <span className="w-8 shrink-0 text-xs font-semibold text-muted-foreground">
                      {positionLabel}
                    </span>
                    <span
                      className={`min-w-0 flex-1 truncate text-sm font-semibold ${
                        isMyPick &&
                        entry.position !== null &&
                        entry.position <= 3
                          ? "text-accent"
                          : ""
                      }`}
                    >
                      {(isMyPick
                        ? pickSnapshots.get(entry.playerId)
                        : undefined) ?? entry.playerName}
                    </span>
                    {isMyPick && (
                      <span
                        className="flex shrink-0 items-center rounded-full border border-accent px-1.5 py-0.5 text-accent"
                        aria-label="Seu palpite"
                      >
                        <Star size={9} fill="currentColor" />
                      </span>
                    )}
                    <RoundStatusCell entry={entry} />
                    <span className="w-9 shrink-0 text-right text-sm font-semibold">
                      {formatScore(entry.totalScore)}
                    </span>
                  </div>
                );
              })}
              {picksBelowTop.length > 0 && (
                <>
                  <div className="flex items-center gap-1.5 border-t border-border bg-surface-alt/60 px-4 py-2">
                    <Star size={11} className="text-accent" fill="currentColor" />
                    <p className="text-[10px] font-bold tracking-wider text-muted-foreground">
                      SEUS PALPITES
                    </p>
                  </div>
                  {picksBelowTop.map(({ pid, snapshotName, entry }, idx) => {
                    const isCut = !entry || entry.position === null;
                    // Mesma regra de empate do Top, dentro da sublista.
                    const positionLabel =
                      entry &&
                      entry.position !== null &&
                      (idx === 0 ||
                        picksBelowTop[idx - 1]!.entry?.position !==
                          entry.position)
                        ? formatPosition(entry.position)
                        : "";
                    return (
                      <div
                        key={pid}
                        className="flex items-center gap-2 border-t border-border px-4 py-2.5"
                      >
                        <span className="w-8 shrink-0 text-xs font-semibold text-muted-foreground">
                          {positionLabel}
                        </span>
                        <span
                          className={`min-w-0 flex-1 truncate text-sm font-semibold ${
                            isCut
                              ? "text-muted-foreground"
                              : entry &&
                                  entry.position !== null &&
                                  entry.position <= 3
                                ? "text-accent"
                                : ""
                          }`}
                        >
                          {snapshotName ?? entry?.playerName ?? pid}
                        </span>
                        {entry && <RoundStatusCell entry={entry} />}
                        <span
                          className={`w-9 shrink-0 text-right text-sm font-semibold ${
                            isCut ? "text-destructive" : ""
                          }`}
                        >
                          {isCut ? "CUT" : formatScore(entry?.totalScore)}
                        </span>
                      </div>
                    );
                  })}
                </>
              )}
            </Card>
          )}
        </section>
      )}

    </div>
  );
}
